import React, { useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Download, Palette } from 'lucide-react';

const QRCodeGenerator = () => {
    const [url, setUrl] = useState('');
    const [fgColor, setFgColor] = useState('#000000');
    const [bgColor, setBgColor] = useState('#FFFFFF');
    const [size, setSize] = useState(256);
    const [includeMargin, setIncludeMargin] = useState(true);
    const [isTransparent, setIsTransparent] = useState(false);
    const qrRef = useRef();

    const downloadQR = () => {
        const qrCodeElement = qrRef.current?.querySelector('canvas');
        if (!qrCodeElement) return;

        const link = document.createElement('a');
        link.download = 'qrcode.png';
        link.href = qrCodeElement.toDataURL('image/png');
        link.click();
    };

    return (
        <div className="container mt-4">
            <div className="row">
                <div className="col-md-6">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="mb-0">Générateur de QR Code</h5>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <label htmlFor="url" className="form-label">URL</label>
                                <input
                                    type="url"
                                    className="form-control"
                                    id="url"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="https://example.com"
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="fgColor" className="form-label d-flex align-items-center">
                                    <Palette className="me-2" size={18} />
                                    Couleur QR Code
                                </label>
                                <input
                                    type="color"
                                    className="form-control form-control-color w-100"
                                    id="fgColor"
                                    value={fgColor}
                                    onChange={(e) => setFgColor(e.target.value)}
                                    title="Choisir la couleur du QR Code"
                                />
                            </div>

                            <div className="mb-3 form-check form-switch">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="isTransparent"
                                    checked={isTransparent}
                                    onChange={(e) => setIsTransparent(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="isTransparent">
                                    Fond transparent
                                </label>
                            </div>

                            {!isTransparent && (
                                <div className="mb-3">
                                    <label htmlFor="bgColor" className="form-label d-flex align-items-center">
                                        <Palette className="me-2" size={18} />
                                        Couleur de fond
                                    </label>
                                    <input
                                        type="color"
                                        className="form-control form-control-color w-100"
                                        id="bgColor"
                                        value={bgColor}
                                        onChange={(e) => setBgColor(e.target.value)}
                                        title="Choisir la couleur de fond"
                                    />
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="size" className="form-label">Taille ({size}px)</label>
                                <input
                                    type="range"
                                    className="form-range"
                                    id="size"
                                    min="128"
                                    max="512"
                                    step="32"
                                    value={size}
                                    onChange={(e) => setSize(Number(e.target.value))}
                                />
                            </div>

                            <div className="mb-3 form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="includeMargin"
                                    checked={includeMargin}
                                    onChange={(e) => setIncludeMargin(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="includeMargin">
                                    Inclure les marges
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="mb-0">Aperçu</h5>
                        </div>
                        <div className="card-body text-center" ref={qrRef}>
                            {url ? (
                                <>
                                    <div style={{ 
                                        background: 'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAALEgAACxIB0t1+/AAAABZ0RVh0Q3JlYXRpb24gVGltZQAxMC8yOS8xMiKqq3kAAAAcdEVYdFNvZnR3YXJlAEFkb2JlIEZpcmV3b3JrcyBDUzVxteM2AAABHklEQVQ4jbWTvUoDQRSFv5ldXURXBBELwcLKwsJWsLLwARQsLX0EX0AQkrcQbGxsBAsLwcISFBHBP4IasrlzLDLJTpYQFkXwwMA9d+Z8596z91r7M4UQEGlnAaEVEh2/E1mCdV2quilXnS+WUFSqeh1AxFoLc+AC+N4DPgJ3xrnNjJaxACLSAfa6Cf9UoAqc1AOmwGxsYmmGYVQFY0BTRI5F5AhYB/Yi+SuQhBB6IvIBXIrIOXAPjJrGr9WSO7+q3arqg6pehlkTVT1rz1u9xaqWmEFEtsATsAu8A8/AO9ASkS3gBJiLSNv3/SQPkGUZwKExZgP4BO5CCCMRSYEd4NXzvL/C5K9/FvugqrbkZiBXvQbmG02d+ZvpF3bR58YoLg8NAAAAAElFTkSuQmCC")',
                                        padding: '20px',
                                        borderRadius: '4px',
                                        display: 'inline-block'
                                    }}>
                                        <QRCodeCanvas
                                            value={url}
                                            size={size}
                                            fgColor={fgColor}
                                            bgColor={isTransparent ? 'transparent' : bgColor}
                                            includeMargin={includeMargin}
                                            level="H"
                                        />
                                    </div>
                                    <div className="mt-3">
                                        <button
                                            className="btn btn-primary"
                                            onClick={downloadQR}
                                            disabled={!url}
                                        >
                                            <Download className="me-2" size={18} />
                                            Télécharger
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="text-muted">
                                    Entrez une URL pour générer le QR Code
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QRCodeGenerator;